/**
 * Session-scoped "last feed visit" semantics for the Update Feed (SE-4865 /
 * SE-4996).
 *
 * Captures the user's stored `lastFeedVisitAt` exactly once on session start
 * so the New-vs-Previous bucket stays stable across in-session navigation.
 * Session = the lifetime of this hook's mount (tab lifetime on web; logout /
 * hard reload starts a new session and re-captures the snapshot).
 *
 * SE-4996: the boundary is NO LONGER committed by a client-side timer. The
 * old "commit `at: now` after 3 cumulative in-app seconds" behavior set the
 * boundary far too early — any activity created later in the session stayed
 * "New" forever, because the next session snapshotted that 3-seconds-in
 * timestamp instead of the moment the user actually left. The boundary is
 * now advanced server-side on the session-end signal:
 *   - graceful close / reload / background → the `markSeenBeacon` Lambda sets
 *     `lastFeedVisitAt = now` (driven by `useSessionEndBeacon`), and
 *   - hard-kill (no beacon) → the `SessionBoundaryInterceptor` advances it on
 *     the next-open >10-minute boundary.
 * Both writes are monotonic on the BE. This hook is therefore read-only: it
 * snapshots the boundary at session start and exposes it for bucketing.
 * @module features/update-feed/hooks/useSessionLastFeedVisit
 */
import { useEffect, useState } from "react";

import { useGetCurrentLastFeedVisitAtQuery } from "@/generated/graphql";

/** Props for `useSessionLastFeedVisit`. */
export interface UseSessionLastFeedVisitProps {
  /**
   * Gates the hook's effects. Pass `true` once sign-in has settled so the
   * query does not fire before auth resolves. The hook itself is always
   * mounted (per Rules of Hooks); when `enabled` is `false` no query fires.
   */
  readonly enabled: boolean;
}

/** Return shape of `useSessionLastFeedVisit`. */
export interface UseSessionLastFeedVisitReturn {
  /**
   * The session's stored `lastFeedVisitAt` snapshot, captured once on
   * session start. Stable across in-session navigation. `null` until the
   * initial query resolves; `null` thereafter for users who have never had a
   * boundary recorded (first-ever session — the BE returns `null`).
   */
  readonly sessionLastFeedVisitAt: string | null;
  /**
   * Whether the initial bucket-capture query has resolved. Useful for
   * gating header counts that depend on the bucket so they don't render a
   * transient "0 new" state during the first paint.
   */
  readonly isReady: boolean;
}

/**
 * SE-4865 / SE-4996 session-start bucket capture hook (read-only).
 * @param props - Hook configuration
 * @param props.enabled - Whether the hook should fire its query
 * @returns The captured session bucket + readiness flag
 */
export function useSessionLastFeedVisit({
  enabled,
}: UseSessionLastFeedVisitProps): UseSessionLastFeedVisitReturn {
  const { data, loading, error } = useGetCurrentLastFeedVisitAtQuery({
    skip: !enabled,
    // Capture once: don't re-run on cache invalidation / refocus. The
    // session bucket must stay stable across in-session navigation per
    // SE-4865 AC.
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-only",
  });

  // Snapshot the very first non-loading query result so subsequent cache
  // changes (e.g. a server-side boundary advance writing back) don't perturb
  // the bucket. `sessionCaptured` is state (not a ref) so a `null` BE result
  // still re-renders to flip `isReady` to true.
  const [sessionLastFeedVisitAt, setSessionLastFeedVisitAt] = useState<
    string | null
  >(null);
  const [sessionCaptured, setSessionCaptured] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (loading) return;
    // A query error must not be misclassified as a null/first-ever-visit
    // bucket. Bail out and leave sessionCaptured false until a successful
    // read is available.
    if (error) return;
    // CRITICAL (SE-4996): wait for the response to actually arrive before
    // latching. `useGetCurrentLastFeedVisitAtQuery` uses
    // `nextFetchPolicy: "cache-only"`, which yields a transient
    // `{ loading: false, data: undefined }` render BEFORE the network
    // result lands. Gating only on `!loading` would latch `null` from that
    // transient and never pick up the real boundary — leaving
    // `sessionLastFeedVisitAt` permanently null so every event buckets as
    // "New" (the bug behind "nothing moves to Previous"). `data` is
    // `undefined` only while in flight; a first-ever-session user gets a
    // defined `{ getCurrentLastFeedVisitAt: null }`, which we DO capture.
    if (data === undefined) return;
    if (sessionCaptured) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- canonical "external-state mirror" pattern: snapshot the BE-supplied bucket value once on session start; subsequent cache writes must not re-shift the captured snapshot
    setSessionLastFeedVisitAt(data.getCurrentLastFeedVisitAt ?? null);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pairs with the above: latches isReady=true so consumers can render bucket-derived UI without a transient "loading" state for null-bucket users
    setSessionCaptured(true);
  }, [enabled, loading, error, sessionCaptured, data]);

  return {
    sessionLastFeedVisitAt,
    isReady: enabled ? sessionCaptured : true,
  };
}
